import { getLocationOrThrow } from "@faire/web/common/globals/getLocation";
import { getCurrentPath, Path } from "@faire/web/common/Path";

/**
 * Generates a url using the current path as base with the page as a query param.
 *
 * Omits the first page (page == 1)
 *
 * i.e `/current-path?page=2`
 *
 * @param pageNumber
 */
export const buildPageLink = (pageNumber: number): string => {
  const path = getCurrentPath().delete("page");
  if (pageNumber === 1) {
    return path.toString();
  }
  return path.append({ page: pageNumber.toString() }).toString();
};

/**
 * Generates a url using the current path as base by updating the passed param
 * and removes all other passed params.
 *
 * Omits if the param value is an empty string.
 *
 * ```
 * // e.g given `/current-path?query=here&page=2&sort=new`
 * buildParamLink("query", "updateQuery", ["sort"])
 * // returns `/current-path?query=updateQuery&page=2`
 * ```
 *
 * @param paramName query param key to be updated
 * @param paramValue query param value use
 * @param otherParamsToRemove list of query param keys to remove. All other params will be remain untouched
 */
export const buildParamLink = (
  paramName: string,
  paramValue: string,
  otherParamsToRemove: string[] = []
): string => {
  const path = getCurrentPath().delete(paramName);

  otherParamsToRemove.forEach((param) => path.delete(param));

  if (paramValue !== "") {
    path.append({ [paramName]: paramValue });
  }

  return path.toString();
};

/**
 * Generates a URL using the given localized url and an object map of query parameters
 *
 * ```
 * // e.g given localizedUrl = `/current-path?query=here&page=2&sort=new`, queryParams = {referrer: "google"}
 * // returns `/current-path?query=updateQuery&sort=new&referrer=google`
 * ```
 *
 * @param localizedUrl url
 * @param queryParams mapping of query params to add
 */
export const localizedUrlWithQueryParams = (
  localizedUrl: string,
  queryParams: { [key: string]: string | undefined }
): string => {
  return new Path(new URL(getLocationOrThrow().origin + localizedUrl))
    .append(queryParams)
    .toString();
};
